import { useState } from 'react';
import type { DivisionProfile, DivisionBaseline, CategoryProfile } from '@/lib/types/division-profile';
import { deriveStrengthsAndWeaknesses, type StrengthItem } from '@/lib/analysis/division-briefing-traits';
import { DivisionTraitsExplainerDialog } from './DivisionTraitsExplainerDialog';
import { SlotCostGrid } from '@/components/SlotCostGrid';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import {
  Truck,
  Users,
  Shield,
  Crosshair,
  ShieldAlert,
  Target,
  Flame,
  Plane,
  Info,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface DivisionSummaryPanelProps {
  profile: DivisionProfile;
  baseline: DivisionBaseline;
}

const categories: { key: keyof Pick<DivisionProfile, 'logistics' | 'infantry' | 'tanks' | 'recon' | 'antiAir' | 'artillery' | 'helicopters' | 'planes'>; label: string; icon: LucideIcon }[] = [
  { key: 'logistics', label: 'LOG', icon: Truck },
  { key: 'infantry', label: 'INF', icon: Users },
  { key: 'tanks', label: 'TNK', icon: Shield },
  { key: 'recon', label: 'REC', icon: Crosshair },
  { key: 'antiAir', label: 'AA', icon: ShieldAlert },
  { key: 'artillery', label: 'ART', icon: Target },
  { key: 'helicopters', label: 'HEL', icon: Flame },
  { key: 'planes', label: 'AIR', icon: Plane },
];

function StrengthPill({ item }: { item: StrengthItem }) {
  const isStrength = item.type === 'strength';
  const pill = (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border',
        isStrength
          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
          : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
      )}
    >
      {item.label}
    </span>
  );

  if (!item.tooltipText) return pill;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{pill}</TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs max-w-56">
        {item.tooltipText}
      </TooltipContent>
    </Tooltip>
  );
}

export function DivisionSummaryPanel({ profile, baseline }: DivisionSummaryPanelProps) {
  const [traitsOpen, setTraitsOpen] = useState(false);
  const items = deriveStrengthsAndWeaknesses(profile, baseline);
  const strengths = items.filter((i) => i.type === 'strength');
  const weaknesses = items.filter((i) => i.type === 'weakness');

  return (
    <>
      <div className="rounded-xl border bg-card text-card-foreground shadow-sm overflow-hidden">
        <div className="px-2 py-1.5">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Division Composition
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px] gap-1 text-muted-foreground hover:text-foreground shrink-0"
              onClick={() => setTraitsOpen(true)}
            >
              <Info className="h-3.5 w-3.5" aria-hidden />
              Explain traits
            </Button>
          </div>
          <div className="flex flex-col gap-0.5">
            {categories.map(({ key, label, icon: Icon }) => {
              const cat = profile[key] as CategoryProfile;
              return (
                <div key={key} className="flex items-center gap-2 min-w-0">
                  <div className="flex items-center gap-1 w-12 shrink-0">
                    <Icon className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="text-[11px] font-semibold tracking-wide">
                      {label}
                    </span>
                  </div>
                  <SlotCostGrid costs={cat.slots.activationCosts} />
                </div>
              );
            })}
          </div>
        </div>

        {(strengths.length > 0 || weaknesses.length > 0) && (
          <div className="px-2 py-1.5 border-t bg-muted/20">
            <div className="flex flex-wrap gap-1.5">
              {strengths.map((s, i) => (
                <StrengthPill key={`s-${i}-${s.label}`} item={s} />
              ))}
              {weaknesses.map((w, i) => (
                <StrengthPill key={`w-${i}-${w.label}`} item={w} />
              ))}
            </div>
          </div>
        )}
      </div>

      <DivisionTraitsExplainerDialog
        profile={profile}
        baseline={baseline}
        open={traitsOpen}
        onOpenChange={setTraitsOpen}
      />
    </>
  );
}
